import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MarriottWakeUpAlarmWidget, ProjectCMSWidgetType, LogService, SpecificActionSubType } from 'htv-lib'
import { BaseWidgetComponent } from '../base-widget/base-widget.component'
import { StateService } from '../../services/state.service'
import { AlarmService } from '../../services/alarm.service'
import { WakeUpAlarmConfig } from '../../interfaces/location'
import { TranslateModule } from '@ngx-translate/core'
import { WidgetActionService } from '../../services/widget-action.service'
import { LocationService } from '../../services/location.service'
import { EventService } from '../../services/event.service'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'

type WidgetMode = 'list' | 'edit'

@Component({
  selector: 'app-marriott-wake-up-alarm',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './marriott-wake-up-alarm.component.html',
  styleUrls: ['./marriott-wake-up-alarm.component.scss'],
})
export class MarriottWakeUpAlarmComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  mode: WidgetMode = 'list'
  alarms: WakeUpAlarmConfig[] = []

  digits = [0, 7, 0, 0]
  selectedDigitIndex = 0
  isPM = false
  isSaving = false
  editingAlarmId?: string
  focusedAlarmId?: string

  constructor(
    private alarmService: AlarmService,
    private stateService: StateService,
    private locationService: LocationService,
    private eventService: EventService,
    private appButtonController: HTVButtonControllerDirective,
    private renderer: Renderer2,
    private logService: LogService,
    private widgetActionService: WidgetActionService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()
    this.loadAlarms()
    this.registerEventListener()
    this.stateService.startShowingWidget(ProjectCMSWidgetType.MARRIOTT_WAKE_UP_ALARM)
    this.registerWidgetActionListener()
  }

  registerWidgetActionListener(): void {
    this.subscriptions.add(
      this.widgetActionService.wakeUpAlarmWidgetAction$.subscribe(action => {
        this.handleWidgetAction(action)
      })
    )
  }

  handleWidgetAction(action: SpecificActionSubType): void {
    this.logService.debug(`[MarriottWakeUpAlarmComponent] handleWidgetAction: ${action}`)
    switch (action) {
      case SpecificActionSubType.ALARM_CREATE:
        this.logService.debug('[MarriottWakeUpAlarmComponent] ALARM_CREATE received, entering edit mode')
        this.enterEditMode()
        break
    }
  }

  override ngOnDestroy(): void {
    this.stateService.stopShowingWidget(ProjectCMSWidgetType.MARRIOTT_WAKE_UP_ALARM)
    super.ngOnDestroy()
  }

  // ==================== LIST MODE ====================

  loadAlarms(): void {
    this.alarms = this.sortAlarmsByNextTrigger(this.alarmService.wakeupAlarmConfigs)
    this.subscriptions.add(
      this.locationService.location$.subscribe(() => {
        this.alarms = this.sortAlarmsByNextTrigger(this.alarmService.wakeupAlarmConfigs)
      })
    )
  }

  private sortAlarmsByNextTrigger(alarms: WakeUpAlarmConfig[]): WakeUpAlarmConfig[] {
    const now = new Date()
    return [...alarms].sort((a, b) => {
      const nextTriggerA = this.getNextTriggerTime(a.time, now)
      const nextTriggerB = this.getNextTriggerTime(b.time, now)
      return nextTriggerA.getTime() - nextTriggerB.getTime()
    })
  }

  private getNextTriggerTime(time: string, now: Date): Date {
    const [alarmHours, alarmMinutes] = time.split(':').map(Number)
    const alarmDate = new Date(now)
    alarmDate.setHours(alarmHours, alarmMinutes, 0, 0)

    const alarmTotalMinutes = alarmHours * 60 + alarmMinutes
    const currentTotalMinutes = now.getHours() * 60 + now.getMinutes()

    if (alarmTotalMinutes < currentTotalMinutes) {
      alarmDate.setDate(alarmDate.getDate() + 1)
    }
    return alarmDate
  }

  formatTime12h(time: string): string {
    const [hours24, minutes] = time.split(':').map(Number)
    let hours12 = hours24 % 12
    if (hours12 === 0) hours12 = 12
    const ampm = hours24 >= 12 ? 'PM' : 'AM'
    return `${String(hours12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${ampm}`
  }

  getAlarmDateDisplay(alarm: WakeUpAlarmConfig): string {
    if (!alarm.time) return ''
    const [alarmHours, alarmMinutes] = alarm.time.split(':').map(Number)
    const now = new Date()
    const currentHours = now.getHours()
    const currentMinutes = now.getMinutes()

    const alarmTotalMinutes = alarmHours * 60 + alarmMinutes
    const currentTotalMinutes = currentHours * 60 + currentMinutes

    if (alarmTotalMinutes < currentTotalMinutes) {
      return 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.TOMORROW'
    }
    return 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.TODAY'
  }

  enterEditMode(alarm?: WakeUpAlarmConfig): void {
    this.mode = 'edit'
    this.editingAlarmId = alarm?.id

    if (alarm?.time) {
      const [hours24, minutes] = alarm.time.split(':').map(Number)
      this.loadTimeIntoDigits(hours24, minutes)
    } else {
      this.resetToDefaults()
    }
  }

  deleteAlarm(alarmId: string): void {
    this.subscriptions.add(
      this.alarmService.deleteAlarm(alarmId).subscribe({
        next: alarms => {
          this.alarms = this.sortAlarmsByNextTrigger(alarms)
          this.logService.debug(`Alarm ${alarmId} deleted`)
          this.appButtonController.forceRefreshSelectedButton()
        },
        error: err => {
          this.logService.error(`Failed to delete alarm: ${err}`)
        },
      })
    )
  }

  onAlarmFocus(alarmId: string): void {
    this.focusedAlarmId = alarmId
  }

  trackByAlarmId(index: number, alarm: WakeUpAlarmConfig): string {
    return alarm.id || index.toString()
  }

  // ==================== EDIT MODE ====================

  private resetToDefaults(): void {
    this.digits = [0, 7, 0, 0]
    this.isPM = false
  }

  private loadTimeIntoDigits(hours24: number, minutes: number): void {
    if (hours24 === 0) {
      this.digits[0] = 1
      this.digits[1] = 2
      this.isPM = false
    } else if (hours24 < 12) {
      this.digits[0] = Math.floor(hours24 / 10)
      this.digits[1] = hours24 % 10
      this.isPM = false
    } else if (hours24 === 12) {
      this.digits[0] = 1
      this.digits[1] = 2
      this.isPM = true
    } else {
      const hours12 = hours24 - 12
      this.digits[0] = Math.floor(hours12 / 10)
      this.digits[1] = hours12 % 10
      this.isPM = true
    }

    this.digits[2] = Math.floor(minutes / 10)
    this.digits[3] = minutes % 10
  }

  registerEventListener(): void {
    this.subscriptions.add(
      this.eventService.keyDown.subscribe(key => {
        if (this.mode === 'edit') {
          this.handleKeyPress(key)
        }
      })
    )
  }

  handleKeyPress(key: string): void {
    this.updateSelectedDigitIndex()

    if (key >= '0' && key <= '9') {
      const digit = parseInt(key)
      if (this.isValidDigit(digit)) {
        this.digits[this.selectedDigitIndex] = digit
        this.validateAndResetFollowingDigits()
      }
      return
    }

    if (key === 'COL-RED' || key === 'r') {
      this.isPM = !this.isPM
      return
    }
  }

  isValidDigit(digit: number): boolean {
    switch (this.selectedDigitIndex) {
      case 0:
        return digit <= 1
      case 1:
        if (this.digits[0] === 0) {
          return digit >= 1 && digit <= 9
        } else {
          return digit <= 2
        }
      case 2:
        return digit <= 5
      case 3:
        return true
      default:
        return false
    }
  }

  onDigitClick(index: number): void {
    this.selectedDigitIndex = index
    this.moveToNextDigit()
  }

  updateSelectedDigitIndex(): void {
    const digitBoxes = this.el.nativeElement.querySelectorAll('.digit-box')
    digitBoxes.forEach((box: HTMLElement, index: number) => {
      if (box.classList.contains('htv-selected')) {
        this.selectedDigitIndex = index
      }
    })
  }

  moveToNextDigit(): void {
    const nextIndex = (this.selectedDigitIndex + 1) % 4
    const digitBoxes = this.el.nativeElement.querySelectorAll('.digit-box')

    if (digitBoxes[this.selectedDigitIndex]) {
      this.renderer.removeClass(digitBoxes[this.selectedDigitIndex], 'htv-focus-99')
    }

    if (digitBoxes[nextIndex]) {
      this.renderer.addClass(digitBoxes[nextIndex], 'htv-focus-99')
    }

    this.selectedDigitIndex = nextIndex
  }

  validateAndResetFollowingDigits(): void {
    if (this.selectedDigitIndex === 0) {
      const currentUnits = this.digits[1]
      if (this.digits[0] === 0) {
        if (currentUnits === 0) {
          this.digits[1] = 1
        }
      } else if (this.digits[0] === 1) {
        if (currentUnits > 2) {
          this.digits[1] = 0
        }
      }
    }
  }

  saveAlarm(): void {
    if (this.isSaving) return

    const config = this.buildAlarmConfig()

    if (this.editingAlarmId) {
      this.updateExistingAlarm(config)
    } else {
      this.createNewAlarm(config)
    }
  }

  private createNewAlarm(config: { time: string; channelId?: string }): void {
    if (this.alarmService.wakeupAlarmConfigs.length >= this.alarmService.MAX_ALARMS) {
      this.logService.error('Maximum number of alarms reached')
      return
    }

    this.isSaving = true
    this.subscriptions.add(
      this.alarmService.createAlarm(config).subscribe({
        next: alarms => {
          this.logService.debug('Alarm created successfully')
          this.alarms = this.sortAlarmsByNextTrigger(alarms)
          this.isSaving = false
          this.editingAlarmId = undefined
          this.mode = 'list'
          this.appButtonController.forceRefreshSelectedButton()
        },
        error: err => {
          this.logService.error(`Failed to create alarm: ${err}`)
          this.isSaving = false
        },
      })
    )
  }

  private updateExistingAlarm(config: { time: string; channelId?: string }): void {
    if (!this.editingAlarmId) return

    this.isSaving = true
    this.subscriptions.add(
      this.alarmService.updateAlarm(this.editingAlarmId, config).subscribe({
        next: alarms => {
          this.logService.debug('Alarm updated successfully')
          this.alarms = this.sortAlarmsByNextTrigger(alarms)
          this.isSaving = false
          this.editingAlarmId = undefined
          this.mode = 'list'
          this.appButtonController.forceRefreshSelectedButton()
        },
        error: err => {
          this.logService.error(`Failed to update alarm: ${err}`)
          this.isSaving = false
        },
      })
    )
  }

  buildAlarmConfig(): { time: string; channelId?: string } {
    const hours24 = this.convertTo24h()
    const minutes = this.digits[2] * 10 + this.digits[3]
    const timeString = `${String(hours24).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    return { time: timeString, channelId: undefined }
  }

  convertTo24h(): number {
    const hours12 = this.digits[0] * 10 + this.digits[1]
    if (this.isPM) {
      return hours12 === 12 ? 12 : hours12 + 12
    } else {
      return hours12 === 12 ? 0 : hours12
    }
  }

  get editTitle(): string {
    return this.editingAlarmId
      ? 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.TITLE_EDIT'
      : 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.TITLE_CREATE'
  }

  get saveButtonText(): string {
    if (this.isSaving) {
      return 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.SAVING'
    }
    return this.editingAlarmId
      ? 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.UPDATE_ALARM'
      : 'WIDGETS.MARRIOTT_WAKE_UP_ALARM.SAVE_ALARM'
  }

  get canAddMoreAlarms(): boolean {
    return this.alarmService.wakeupAlarmConfigs.length < this.alarmService.MAX_ALARMS
  }

  get canSaveAlarm(): boolean {
    return !!this.editingAlarmId || this.canAddMoreAlarms
  }

  // ==================== STYLES ====================

  override applyStyles(): void {
    super.applyStyles()

    const config = this.config as MarriottWakeUpAlarmWidget
    const widgetStyle: Record<string, string | number> = {}
    const redButtonStyle: Record<string, string | number> = {}
    const alarmButtonStyle: Record<string, string | number> = {}
    const alarmButtonSelectedStyle: Record<string, string | number> = {}
    const maxAlarmsMessageStyle: Record<string, string | number> = {}
    const editButtonStyle: Record<string, string | number> = {}
    const editButtonSelectedStyle: Record<string, string | number> = {}
    const digitBoxSelectedStyle: Record<string, string | number> = {}
    const indicatorStyle: Record<string, string | number> = {}
    const indicatorBeforeStyle: Record<string, string | number> = {}

    if (config.backgroundColor) {
      widgetStyle['background-color'] = config.backgroundColor
    }

    if (config.textColor) {
      widgetStyle['color'] = config.textColor
      redButtonStyle['border-color'] = config.textColor
      alarmButtonStyle['color'] = config.textColor
      maxAlarmsMessageStyle['color'] = config.textColor
      indicatorStyle['border-color'] = config.textColor
    }

    if (config.buttonColor) {
      alarmButtonStyle['border-color'] = config.buttonColor
      alarmButtonStyle['background-color'] = config.buttonColor
      alarmButtonSelectedStyle['box-shadow'] = `0 0 15px ${config.buttonColor}`
      editButtonStyle['background-color'] = config.buttonColor
      editButtonStyle['border-color'] = config.buttonColor
      editButtonSelectedStyle['box-shadow'] = `0 0 15px ${config.buttonColor}`
      digitBoxSelectedStyle['border-color'] = config.buttonColor
      digitBoxSelectedStyle['box-shadow'] = `0 0 15px ${config.buttonColor}`
      indicatorBeforeStyle['background-color'] = config.buttonColor
    }

    this.setRule('.wake-up-alarm-widget', widgetStyle)
    this.setRule('.red-button', redButtonStyle)
    this.setRule('.alarm-button', alarmButtonStyle)
    this.setRule('.alarm-button.htv-selected', alarmButtonSelectedStyle)
    this.setRule('.max-alarms-message', maxAlarmsMessageStyle)
    this.setRule('.edit-button', editButtonStyle)
    this.setRule('.edit-button.htv-selected', editButtonSelectedStyle)
    this.setRule('.digit-box.htv-selected', digitBoxSelectedStyle)
    this.setRule('.indicator', indicatorStyle)
    this.setRule('.indicator::before', indicatorBeforeStyle)
  }
}
